/* ============================================================
   CRO PDP — Apple-style accordion animation
   ------------------------------------------------------------
   The theme's PDP accordions are native <details class="collapsible-tab">.
   Native <details> snaps open/closed because the UA stylesheet sets
   display:none on non-summary children when [open] is absent. We intercept
   the <summary> click, hold [open] while animating, and tween the
   .collapsible-tab__text height/opacity/padding between 0 and the
   measured natural height with cubic-bezier(.32,.72,0,1) (the Apple curve).
   Pairs with the matching transition declarations in assets/cro-pdp.css.
   ============================================================ */
(function () {
  var EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
  var DUR = 420;

  // Keep `el` visually pinned while the accordion re-flows around it.
  // Opening a tab collapses whichever one was open, so the document height
  // changes — if the collapsing panel sits above the clicked row, the row is
  // yanked upward out from under the cursor, and when the document shrinks the
  // browser can clamp scrollY and shove everything down instead. Both read as
  // "the page jumped". We re-measure each frame for the length of the
  // transition and cancel the drift, releasing immediately if the user starts
  // scrolling themselves (wheel/touch/key — NOT the scroll event, which our own
  // scrollBy would trigger).
  function anchor(el, ms) {
    var start = el.getBoundingClientRect().top;
    var released = false;
    var release = function () { released = true; };
    var opts = { passive: true };
    window.addEventListener('wheel', release, opts);
    window.addEventListener('touchmove', release, opts);
    window.addEventListener('keydown', release, opts);

    var cleanup = function () {
      window.removeEventListener('wheel', release, opts);
      window.removeEventListener('touchmove', release, opts);
      window.removeEventListener('keydown', release, opts);
    };

    var t0 = performance.now();
    (function frame() {
      if (released) { cleanup(); return; }
      var delta = el.getBoundingClientRect().top - start;
      if (delta) window.scrollBy(0, delta);
      if (performance.now() - t0 < ms) requestAnimationFrame(frame);
      else cleanup();
    })();
  }

  // Every OTHER panel in the same group. Used to enforce one-open-at-a-time.
  function siblings(d, opts) {
    var scope = (d.closest && d.closest(opts.scope)) || document;
    return Array.prototype.filter.call(
      scope.querySelectorAll(opts.item),
      function (t) { return t !== d; }
    );
  }

  // Binds one set of <details> accordions. `opts`:
  //   item      – selector for the <details> elements in the group
  //   scope     – ancestor selector delimiting one group
  //   summary   – selector for the clickable header inside the <details>
  //   content   – selector for the panel to animate
  //   exclusive – true to collapse the rest of the group when one opens
  function bindGroup(opts, reduced) {
    Array.prototype.forEach.call(document.querySelectorAll(opts.item), function (d) {
      if (d.dataset.croAccBound) return;
      d.dataset.croAccBound = '1';

      var summary = d.querySelector(opts.summary);
      var content = d.querySelector(opts.content);
      if (!summary || !content) return;

      // store the rule-derived padding so we can animate to/from it precisely
      var cs = window.getComputedStyle(content);
      var padTop = cs.paddingTop;
      var padBot = cs.paddingBottom;

      content.style.transition =
        'height ' + DUR + 'ms ' + EASE +
        ', opacity 0.28s ease' +
        ', padding ' + DUR + 'ms ' + EASE;
      content.style.overflow = 'hidden';
      content.style.willChange = 'height';

      if (!d.open) {
        content.style.height = '0px';
        content.style.opacity = '0';
        content.style.paddingTop = '0px';
        content.style.paddingBottom = '0px';
      }

      // Run `fn` exactly once — on the height transition ending, or on a timer,
      // whichever lands first. The timer is not belt-and-braces, it is load
      // bearing: closing starts from height:auto (set when the previous open
      // finished), and auto→0 is not interpolatable, so no transition runs and
      // `transitionend` never fires. Without the fallback the tab would stay
      // stuck [open] — which is exactly what broke sibling-closing.
      var settle = function (fn) {
        var done = false;
        var finish = function (ev) {
          if (ev && ev.propertyName !== 'height') return;
          if (done) return;
          done = true;
          content.removeEventListener('transitionend', finish);
          clearTimeout(timer);
          fn();
        };
        var timer = setTimeout(finish, DUR + 120);
        content.addEventListener('transitionend', finish);
      };

      // CLOSE: animate from the current natural height down to 0.
      // Exposed on the element so sibling tabs can close this one (see below).
      var closeTab = function () {
        if (!d.open) return;
        // Pin an explicit px start height — never animate out of `auto`.
        content.style.height = content.scrollHeight + 'px';
        content.style.paddingTop = padTop;
        content.style.paddingBottom = padBot;
        // force reflow so the starting frame is committed
        // eslint-disable-next-line no-unused-expressions
        content.offsetHeight;
        requestAnimationFrame(function () {
          content.style.height = '0px';
          content.style.opacity = '0';
          content.style.paddingTop = '0px';
          content.style.paddingBottom = '0px';
        });
        settle(function () {
          d.open = false;
          content.style.height = '0px';
        });
      };
      d.croClose = closeTab;

      var openTab = function () {
        // Flip [open] so the content reflows in the DOM, then animate 0 → natural height
        d.open = true;
        content.style.height = '0px';
        content.style.opacity = '0';
        content.style.paddingTop = '0px';
        content.style.paddingBottom = '0px';

        // Registered OUTSIDE the rAF on purpose. Animation frames don't run in a
        // backgrounded tab, so if this lived inside the frame callback the panel
        // would be left [open] but collapsed to 0 with no pending settle to
        // recover it. Out here, the final open state lands either way — the
        // animation is decoration, this is the state that matters.
        settle(function () {
          // release to auto so the content can reflow if its contents change later
          content.style.height = 'auto';
          content.style.opacity = '1';
          content.style.paddingTop = padTop;
          content.style.paddingBottom = padBot;
        });

        requestAnimationFrame(function () {
          // first frame: pad restored to get an accurate scrollHeight
          content.style.paddingTop = padTop;
          content.style.paddingBottom = padBot;
          var target = content.scrollHeight;
          // collapse again instantly before animating
          content.style.height = '0px';
          // force reflow
          // eslint-disable-next-line no-unused-expressions
          content.offsetHeight;
          requestAnimationFrame(function () {
            content.style.height = target + 'px';
            content.style.opacity = '1';
          });
        });
      };

      summary.addEventListener('click', function (e) {
        e.preventDefault();

        // Pin this header for the duration of the reflow so the row the user
        // just clicked doesn't move under them.
        anchor(summary, DUR + 140);

        // Accordion: only one panel open at a time. Applies to the PDP buy-box
        // tabs; the FAQ is bound non-exclusive so several answers can be open.
        if (opts.exclusive && !d.open) {
          Array.prototype.forEach.call(siblings(d, opts), function (t) {
            if (!t.open) return;
            if (reduced || typeof t.croClose !== 'function') t.open = false;
            else t.croClose();
          });
        }

        if (reduced) { d.open = !d.open; return; }

        if (d.open) closeTab();
        else openTab();
      });
    });
  }

  function init() {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // PDP buy-box tabs — one open at a time.
    bindGroup({
      item: '.product-container details.collapsible-tab',
      scope: '.product-container',
      summary: 'summary, .collapsible-tab__heading',
      content: '.collapsible-tab__text',
      exclusive: true
    }, reduced);

    // CRO FAQ sections (PDP + homepage) — same animation, but several answers
    // may be open at once.
    bindGroup({
      item: 'details.cro-faq__item',
      scope: '.cro-faq',
      summary: 'summary',
      content: '.cro-faq__answer',
      exclusive: false
    }, reduced);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Theme editor: re-run when a section is re-rendered.
  document.addEventListener('shopify:section:load', init);
})();
